import { useCallback, useEffect, useRef, useState } from 'react';
import './styles.scss';
import { useSelector, useDispatch } from 'react-redux'
import { FadersHorizontalIcon, PlusIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { updateHeader } from '../../slices/header-slice';
import { updateProductsData, updateDivergenceData } from '../../slices/app-slice';
import { getProducts, getDivergenceData } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';
import { LazyLoadedGroup } from '../../components/lazy-loaded-group';
import { groupArrayInChunks, createSlug } from '../../utility/utils';
import FilterOverlay from '../../components/filter-overlay';


const Store = () => {
    const navigate = useNavigate();
    const products = useSelector((state) => state.app.products);
    const divergence = useSelector((state) => state.app.divergence);
    const dispatch = useDispatch();
    const [filters, setFilters] = useState([]);
    const [activeFilter, setActiveFilter] = useState(null);
    const [activeStatusFilter, setActiveStatusFilter] = useState(null);
    const [filterResults, setFilterResults] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [priceRange, setPriceRange] = useState([0, Infinity]);
    const sliderRef = useRef(null);

    // Loading state for products data
    const [isProductsLoading, setIsProductsLoading] = useState(false);
    const hasFetched = useRef(false);

    // Register loading state with global spinner
    useDataLoading('store-products', isProductsLoading, []);

    // Fetch products data if not already populated
    useEffect(() => {
        const fetchProductsData = async () => {
            // Check if products have actual data (not just empty array)
            const hasData = products && Array.isArray(products) && products.length > 0;

            if (!hasData && !hasFetched.current) {
                hasFetched.current = true;
                setIsProductsLoading(true);
                try {
                    // Fetch both products and divergence data in parallel
                    const [productsResponse, divergenceResponse] = await Promise.all([
                        getProducts(),
                        getDivergenceData()
                    ]);

                    // Update both data sources
                    dispatch(updateProductsData(productsResponse.data));
                    dispatch(updateDivergenceData(divergenceResponse.data));
                } catch (error) {
                    console.error('Error fetching products or divergence data:', error);
                } finally {
                    setIsProductsLoading(false);
                }
            }
        };

        fetchProductsData();
    }, [products, dispatch]);

    const statusOptions = ['Ready to Ship'];

    const minPrice = products && products.length > 0 ? Math.min(...products.map(p => Math.floor(p.price))) : 0;
    const maxPrice = products && products.length > 0 ? Math.max(...products.map(p => p.price)) : 1000;

    // Build filter configuration for FilterOverlay
    const filterConfig = {
        categories: {
            label: 'Categories',
            type: 'radio',
            options: filters.map(filter => ({
                value: filter,
                label: filter
            }))
        },
        readyToShip: {
            label: 'Status',
            type: 'radio',
            options: statusOptions.map(status => ({
                value: status,
                label: status
            }))
        },
        price: {
            label: 'Price',
            type: 'price'
        }
    };

    const sortConfig = [
        { value: 'highToLow', label: 'High to Low' },
        { value: 'lowToHigh', label: 'Low to High' }
    ];

    const [sortOption, setSortOption] = useState(null);

    useEffect(() => {
        dispatch(updateHeader('light'));


        if (products) {
            let updatedProducts = [...products];  // Copy the original products array

            if (divergence && divergence.divergenceMetadata && divergence.divergenceMetadata.title) {
                updatedProducts.unshift({
                    title: divergence.divergenceMetadata.title,
                    generalImagery: divergence.generalImagery,
                    category: "Furniture",
                    tag: "Divergence",
                    type: "DiningTable",
                    variants: divergence.variantSpecies,
                });
            }

            const categories = updatedProducts.map(x => x.category);
            const unique = [...new Set(categories)].filter(category => category && category.trim() !== '');  // Find unique categories and filter out empty values

            setFilters(unique);
            setFilterResults(groupArrayInChunks(updatedProducts, 8));

        }
    }, [dispatch, products, divergence]);  // Add divergenceDiningTables as a dependency

    const filterProducts = useCallback(() => {
        let updatedProducts = [...products];
        let filteredProducts = updatedProducts;


        if (divergence && divergence.divergenceMetadata && divergence.divergenceMetadata.title) {
            // Combine products and divergenceDiningTables
            filteredProducts.unshift({
                title: divergence.divergenceMetadata.title,
                generalImagery: divergence.generalImagery,
                category: "Furniture",
                tag: "Divergence",
                type: "DiningTable",
                variants: divergence.variantSpecies,
            });
        }

        if (activeFilter) {
            filteredProducts = filteredProducts.filter((product) => product.category === activeFilter);
        }

        if (activeStatusFilter) {
            filteredProducts = filteredProducts.filter((product) => product.status === activeStatusFilter);
        }

        filteredProducts = filteredProducts.filter(
            (product) => (product.type === "DiningTable") || (product.price >= priceRange[0] && product.price <= priceRange[1])
        );

        // Apply sorting after filtering
        if (sortOption) {
            if (sortOption === 'highToLow') {
                filteredProducts.sort((a, b) => {
                    const priceA = a.price || 0;
                    const priceB = b.price || 0;
                    return priceB - priceA;
                });
            } else if (sortOption === 'lowToHigh') {
                filteredProducts.sort((a, b) => {
                    const priceA = a.price || 0;
                    const priceB = b.price || 0;
                    return priceA - priceB;
                });
            }
        }

        setFilterResults(groupArrayInChunks(filteredProducts, 8));
    }, [activeFilter, activeStatusFilter, priceRange, products, divergence, sortOption]);

    useEffect(() => {
        filterProducts();
    }, [filterProducts]);

    const onProductClick = (item) => {
        const titleSlug = createSlug(item.title);

        if (item.type === "DiningTable") {
            navigate({
                pathname: `/divergence`,
                search: `?title=${titleSlug}`,
            })
        } else {
            navigate({
                pathname: `/products/${item.id}`,
                search: `?title=${titleSlug}`,
            })
        }
    };

    const handlePriceChange = (range) => {
        setPriceRange(range);
    };

    const handleFilterApply = useCallback((appliedFilters, appliedSort) => {
        setActiveFilter(appliedFilters.categories || null);
        setActiveStatusFilter(appliedFilters.readyToShip || null);
        setSortOption(appliedSort);
    }, []);

    return (
        <div className="store-page">
            <div className="store-intro">
                <h1>From our studio, to your home.</h1>
                <p>Welcome to our exclusive range of Paramount pieces available to purchase today. An opportunity to purchase your very own piece of the Paramount portfolio, our collection ranges from small tables, home accessories and merchandise. All products and works are made right here in Bucks County PA. </p>
            </div>

            <div className='tools-wrap'>
                <div className='tools'>
                    <div className='filter' onClick={() => setFilterOpen(!filterOpen)}>
                        <span><span> Filters <PlusIcon size={16} /> </span><span>Sort <FadersHorizontalIcon size={24} /></span></span>
                    </div>
                </div>
            </div>

            <FilterOverlay
                isOpen={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                filters={filterConfig}
                activeFilters={{
                    categories: activeFilter,
                    readyToShip: activeStatusFilter
                }}
                sortOptions={sortConfig}
                activeSortOption={sortOption}
                priceRange={priceRange}
                minPrice={minPrice}
                maxPrice={maxPrice}
                onPriceChange={handlePriceChange}
                priceSliderRef={sliderRef}
                title="SHOP"
            />

            <div className='lazy-load'>
                {filterResults?.map((group, i) => (
                    <LazyLoadedGroup
                        key={i}
                        group={group}
                        index={i}
                        type={'products'}
                        onProductClick={onProductClick}
                    />
                ))}
            </div>
        </div>
    );
};

export default Store;
